// Nr listu (nadania / śledzenia) dopisywany PO oznaczeniu wysyłki — w zleceniach wydruku i w
// dzienniku korespondencji. Numer schodzi z potwierdzenia nadania z poczty, które rzadko leży
// na biurku w chwili klikania „Wysłane": w dzienniku numer dawało się dotąd podać wyłącznie
// razem z przejściem statusu, a zlecenie wydruku nie miało na niego miejsca w ogóle.
// Wpisuje go ten, kto pisma wysyła (zlecenia: dowolny użytkownik DLC_02; dziennik: „Firmowy
// obieg dokumentów") — kto konkretnie, rozstrzyga `canEdit` u wywołującego i backend.
import { useState } from "react";
import { Hash, Pencil } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { useRegisterMutations } from "@/features/power_projects/hooks";
import type { CorrespondenceRegister } from "@/features/power_projects/types";
import { usePrintOrderMutations } from "../hooks";
import type { PrintOrder } from "../types";

function errorDetail(error: unknown, fallback: string) {
  return (
    (error as { response?: { data?: { errors?: { detail?: string } } } })?.response?.data?.errors
      ?.detail ?? fallback
  );
}

// Sam numer — pokazywany przy statusie wszystkim, także bez prawa do edycji.
export function TrackingNumberText({ value }: { value: string }) {
  if (!value) return null;
  return (
    <div className="mt-0.5 text-[10px] font-mono text-[var(--color-text-muted)]" title="Nr listu (nadania / śledzenia)">
      {value}
    </div>
  );
}

// Przycisk + rozwijane pole. Wspólna skorupa dla obu modułów; zapis wstrzykuje wywołujący.
function TrackingNumberEditor({ value, pending, error, onSave }: {
  value: string;
  pending: boolean;
  error: unknown;
  onSave: (trackingNumber: string, done: () => void) => void;
}) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(value);

  if (!open) {
    return (
      <Button
        size="sm"
        variant="ghost"
        title={value
          ? "Popraw nr listu (nadania / śledzenia) — zmiana trafia do historii."
          : "Dopisz nr listu (nadania / śledzenia) z potwierdzenia nadania — zmiana trafia do historii."}
        onClick={() => { setDraft(value); setOpen(true); }}
      >
        {value ? <Pencil size={13} /> : <Hash size={13} />}
        {value ? "Zmień nr listu" : "Nr listu"}
      </Button>
    );
  }
  return (
    <div className="w-[220px] border border-[var(--color-border)] border-l-[3px] border-l-[var(--color-primary)] bg-[var(--color-bg-surface)] p-2 space-y-2">
      <label className="block space-y-1">
        <span className="section-label">Nr listu</span>
        <input
          className="input-base text-xs py-1.5 font-mono"
          placeholder="np. RR123456789PL"
          value={draft}
          autoFocus
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && draft.trim()) onSave(draft.trim(), () => setOpen(false));
            if (e.key === "Escape") setOpen(false);
          }}
        />
      </label>
      <div className="flex gap-1.5">
        <Button
          size="sm"
          variant="secondary"
          loading={pending}
          disabled={!draft.trim()}
          onClick={() => onSave(draft.trim(), () => setOpen(false))}
        >
          Zapisz
        </Button>
        <Button size="sm" variant="ghost" onClick={() => setOpen(false)}>Anuluj</Button>
      </div>
      {!!error && (
        <span className="block text-[11px] text-red-600">
          {errorDetail(error, "Nie udało się zapisać nr listu.")}
        </span>
      )}
    </div>
  );
}

// Zlecenie wydruku — po „Wysłane" (i dalej po zwrocie: to ta sama przesyłka).
export function PrintOrderTrackingNumber({ order }: { order: PrintOrder }) {
  const m = usePrintOrderMutations();
  return (
    <TrackingNumberEditor
      value={order.tracking_number}
      pending={m.setTrackingNumber.isPending}
      error={m.setTrackingNumber.error}
      onSave={(trackingNumber, done) =>
        m.setTrackingNumber.mutate({ id: order.id, trackingNumber }, { onSuccess: done })}
    />
  );
}

// Wpis dziennika — pismo wychodzące, które już wyszło.
export function RegisterTrackingNumber({ row }: { row: CorrespondenceRegister }) {
  const m = useRegisterMutations();
  return (
    <TrackingNumberEditor
      value={row.tracking_number}
      pending={m.setTrackingNumber.isPending}
      error={m.setTrackingNumber.error}
      onSave={(trackingNumber, done) =>
        m.setTrackingNumber.mutate({ id: row.id, trackingNumber }, { onSuccess: done })}
    />
  );
}

// Statusy, w których pismo/przesyłka fizycznie wyszła — dopiero wtedy numer nadania istnieje
// (backend pilnuje tego samego zbioru).
export const DISPATCHED_REGISTER_STATUSES = new Set<CorrespondenceRegister["status"]>([
  "sent", "delivered", "returned", "undeliverable",
]);
